/**
 * Kuralin property eslemelerini cozer.
 *
 * Property'ler tanim sirasinda cozulur; bu sayede `js` govdesi kendinden once
 * cozulmus property'leri `props` uzerinden okuyabilir. Senkron kaynaklar aninda,
 * `js` sonuclari `await` ile beklenir; hepsi bitince (veya zaman asiminda) event gonderilir.
 */
import log2 from "../../../js/log2-lite";
import { extractValue, coerce } from "./extract";
import { runJs } from "./jsRunner";
import { hashValue } from "./hash";

const log = log2("TRACKER_RULES");

/**
 * @param {Object} rule derlenmis kural
 * @param {Object} context { el, event, page, visitor, siteId }
 * @param {Object} options { onJsError }
 * @returns {Promise<Object>} gonderilecek props
 */
export function resolveProps(rule, context, options) {
    const opts = options || {};
    const map = rule.map || {};
    const names = Object.keys(map);
    const props = {};
    const ruleInfo = { id: rule.id, name: rule.name, when: rule.when };

    function reportError(propName, error) {
        // Uretimde event akisi bozulmaz; sebep log'a ve editor test moduna dusurulur
        log.warn("property resolve failed", { rule: rule.id, prop: propName, error: error });
        if (typeof opts.onJsError === "function") opts.onJsError(propName, error);
    }

    /** Hash gerekiyorsa uygular, sonra props'a yazar. */
    function assign(propName, mapping, rawValue) {
        const value = coerce(rawValue, mapping.type);
        if (value === null) return Promise.resolve();

        if (mapping.pii === true) {
            return hashValue(context.siteId, value).then(function (hashed) {
                // Hash uretilemediyse ham deger gonderilmez
                if (hashed) props[propName] = hashed;
            });
        }

        props[propName] = value;
        return Promise.resolve();
    }

    function step(index) {
        if (index >= names.length) return Promise.resolve(props);

        const propName = names[index];
        const mapping = map[propName];
        if (!mapping) return step(index + 1);

        if (mapping.from !== "js") {
            let raw = null;
            try {
                raw = extractValue(mapping, context);
            } catch (e) {
                reportError(propName, e.message || "extractFailed");
            }
            return assign(propName, mapping, raw).then(function () {
                return step(index + 1);
            });
        }

        return runJs(rule.id + ":" + propName, mapping.value, {
            el: context.el,
            event: context.event,
            page: context.page,
            visitor: context.visitor,
            rule: ruleInfo,
            props: props,
        })
            .then(function (result) {
                if (!result.ok) {
                    reportError(propName, result.error);
                    return null;
                }
                return assign(propName, mapping, result.value);
            })
            .then(function () {
                return step(index + 1);
            });
    }

    return step(0).then(function () {
        return props;
    });
}

/**
 * Kuralin `if` kosulunu degerlendirir.
 * Kosul yoksa true; hata / zaman asimi durumunda kural tetiklenmez.
 * @returns {Promise<Boolean>}
 */
export function evaluateCondition(rule, context, options) {
    if (!rule.if) return Promise.resolve(true);
    const opts = options || {};

    return runJs(rule.id + ":if", rule.if, {
        el: context.el,
        event: context.event,
        page: context.page,
        visitor: context.visitor,
        rule: { id: rule.id, name: rule.name, when: rule.when },
        props: {},
    }).then(function (result) {
        if (!result.ok) {
            log.warn("condition failed", { rule: rule.id, error: result.error });
            if (typeof opts.onJsError === "function") opts.onJsError("if", result.error);
            return false;
        }
        return !!result.value;
    });
}



//////////////////
// WEBPACK FOOTER
// ./static/src/tracker/rules/resolveProps.js
// module id = null
// module chunks = 